import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Permission, Role } from '../types';

interface PermissionMatrixMobileProps {
  permissions: Permission[];
  roles: Role[];
  rolePermissionDrafts: Record<number, number[]>;
  dirtyRoleIds: number[];
  isRefreshing: boolean;
  pendingAction: string | null;
  onEditPermission: (permission: Permission) => void;
  onRequestDelete: (permission: Permission) => void;
  onTogglePermission: (roleId: number, permissionId: number, checked: boolean) => void;
  onSaveRole: (role: Role) => void;
}

const PermissionMatrixMobile: React.FC<PermissionMatrixMobileProps> = ({
  permissions,
  roles,
  rolePermissionDrafts,
  dirtyRoleIds,
  isRefreshing,
  pendingAction,
  onEditPermission,
  onRequestDelete,
  onTogglePermission,
  onSaveRole,
}) => {
  const { t } = useTranslation();
  const isPermissionMutation = pendingAction === 'permission'
    || pendingAction?.startsWith('permission-delete-') === true;

  return (
    <div className="space-y-3 bg-surface-container-lowest p-3 sm:p-4 lg:hidden">
      <section className="overflow-hidden rounded-xl border border-outline-variant/70 bg-white">
        <div className="border-b border-outline-variant bg-surface-container-low px-4 py-3">
          <h3 className="font-label-md text-on-surface">
            {t('admin.settings.matrix.roles_and_changes')}
          </h3>
        </div>
        <div className="divide-y divide-outline-variant/60">
          {roles.length === 0 && (
            <p className="px-3 py-4 text-center text-body-sm text-secondary">
              {t('admin.settings.matrix.no_roles')}
            </p>
          )}
          {roles.map((role) => {
            const isDirty = dirtyRoleIds.includes(role.id);
            const isSaving = pendingAction === `role-${role.id}`;

            return (
              <div key={role.id} className="flex min-h-14 items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0">
                  <span className="block truncate font-label-md font-semibold text-on-surface">{role.name}</span>
                  <span className="block text-[12px] text-secondary">
                    {t('admin.settings.matrix.permission_count', { count: (rolePermissionDrafts[role.id] ?? []).length })}
                  </span>
                  {isDirty && (
                    <span className="text-[12px] text-primary">
                      {t('admin.settings.matrix.unsaved_changes')}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-xl border border-outline-variant bg-white px-3 font-label-sm text-primary transition-colors hover:bg-primary-fixed disabled:cursor-not-allowed disabled:text-outline"
                  disabled={!isDirty || isRefreshing || pendingAction !== null}
                  onClick={() => onSaveRole(role)}
                >
                  {isSaving && <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>}
                  {isSaving
                    ? t('admin.settings.actions.saving')
                    : t('admin.settings.actions.save_changes')}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {permissions.map((permission) => (
        <article
          key={permission.id}
          className="overflow-hidden rounded-xl border border-outline-variant/70 bg-white"
        >
          <header className="flex items-start justify-between gap-3 border-b border-outline-variant px-3 py-3">
            <div className="min-w-0">
              <h3 className="break-words font-mono text-[13px] font-semibold text-on-surface">
                {permission.name}
              </h3>
              <p className="mt-1 break-words text-body-sm text-secondary">
                {permission.description}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-secondary transition-colors hover:bg-primary-fixed hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
                disabled={isRefreshing || pendingAction !== null}
                aria-label={t('admin.settings.actions.edit_permission_label', {
                  permission: permission.name,
                })}
                onClick={() => onEditPermission(permission)}
              >
                <span className="material-symbols-outlined text-[20px]">edit</span>
              </button>
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-error transition-colors hover:bg-error-container/60 focus:outline-none focus:ring-2 focus:ring-error/30 disabled:opacity-50"
                disabled={isRefreshing || pendingAction !== null}
                aria-label={t('admin.settings.actions.delete_permission_label', {
                  permission: permission.name,
                })}
                onClick={() => onRequestDelete(permission)}
              >
                <span className="material-symbols-outlined text-[20px]">delete</span>
              </button>
            </div>
          </header>

          <div className="grid grid-cols-1 gap-2 bg-white p-3 sm:grid-cols-2">
            {roles.map((role) => {
              const checked = (rolePermissionDrafts[role.id] ?? []).includes(permission.id);
              return (
                <label
                  key={role.id}
                  className="flex min-h-12 cursor-pointer items-center justify-between gap-3 rounded-xl bg-surface-container-lowest px-3 py-2 hover:bg-surface-container-low"
                >
                  <span className="min-w-0 break-words font-label-sm text-on-surface">{role.name}</span>
                  <input
                    type="checkbox"
                    className="h-5 w-5 shrink-0 cursor-pointer accent-primary"
                    checked={checked}
                    disabled={isRefreshing || pendingAction === `role-${role.id}` || isPermissionMutation}
                    aria-label={t('admin.settings.matrix.toggle_label', {
                      permission: permission.name,
                      role: role.name,
                    })}
                    onChange={(event) =>
                      onTogglePermission(role.id, permission.id, event.target.checked)}
                  />
                </label>
              );
            })}
          </div>
        </article>
      ))}
    </div>
  );
};

export default PermissionMatrixMobile;
